import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, ShareNetwork, Crown, TrendUp, Copy, Check } from "@phosphor-icons/react";
import { useWeightUnit } from "@/lib/units";

export default function PersonalRecords({ api, profile }) {
  const { fromKg, label: weightLabel, formatWeight } = useWeightUnit();
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchPRs();
  }, [api]);

  const fetchPRs = async () => {
    try {
      const res = await axios.get(`${api}/personal-records`);
      setPrs(res.data || []);
    } catch (e) {
      console.error("PR fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const sharePR = async (pr) => {
    const cur = formatWeight(pr.weight, { decimals: 1 });
    const prev = pr.previous_weight > 0 ? formatWeight(pr.previous_weight, { decimals: 1 }) : "";
    const text = `New Personal Record on UpTop!\n\n${pr.exercise_display}: ${cur}${prev ? ` (up from ${prev})` : ""}\nSet on ${pr.date}\n\n#UpTop #PersonalRecord #FitnessGoals`;

    if (navigator.share) {
      try {
        await navigator.share({ title: "UpTop PR", text });
        return;
      } catch (e) { /* fallback */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(pr.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) { /* clipboard not available */ }
  };

  const shareAllPRs = async () => {
    const lines = prs.slice(0, 5).map(pr => `  ${pr.exercise_display}: ${formatWeight(pr.weight, { decimals: 1 })}`).join("\n");
    const text = `My Personal Records on UpTop:\n\n${lines}\n\n#UpTop #PersonalRecords #FitnessGoals`;

    if (navigator.share) {
      try {
        await navigator.share({ title: "UpTop PRs", text });
        return;
      } catch (e) { /* fallback */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId("all");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) { /* clipboard not available */ }
  };

  return (
    <div className="space-y-6" data-testid="personal-records-page">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Personal Records
          </h2>
          <p className="text-sm text-[#71717A] mt-1">Your all-time best lifts</p>
        </div>
        {prs.length > 0 && (
          <button
            onClick={shareAllPRs}
            className="bg-transparent border border-[#27272A] hover:border-[#E05A1D] text-[#A1A1AA] hover:text-[#E05A1D] font-bold uppercase tracking-wide px-4 py-2.5 text-xs flex items-center gap-2 transition-all"
            data-testid="share-all-prs-btn"
          >
            {copiedId === "all" ? <Check size={14} weight="bold" /> : <ShareNetwork size={14} weight="bold" />}
            {copiedId === "all" ? "Copied!" : "Share All"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-[#E05A1D] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : prs.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#27272A]" data-testid="no-prs-message">
          <Trophy size={56} className="text-[#27272A] mx-auto mb-4" />
          <p className="text-[#71717A] text-sm mb-1">No personal records yet</p>
          <p className="text-xs text-[#3f3f46]">Log a workout with weights to set your first PR</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pr-cards-grid">
          {prs.map((pr, idx) => (
            <div
              key={pr.id}
              className={`relative border p-5 transition-all group ${
                idx === 0
                  ? "bg-gradient-to-br from-[#E05A1D]/10 to-[#161616] border-[#E05A1D]/40 pr-glow"
                  : idx === 1
                  ? "bg-gradient-to-br from-[#A1A1AA]/5 to-[#161616] border-[#A1A1AA]/20"
                  : idx === 2
                  ? "bg-gradient-to-br from-[#F59E0B]/5 to-[#161616] border-[#F59E0B]/20"
                  : "bg-[#161616] border-[#27272A] hover:border-[#3f3f46]"
              }`}
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`pr-card-${pr.id}`}
            >
              {/* Rank badge */}
              {idx < 3 && (
                <div className={`absolute -top-2 -left-2 w-8 h-8 flex items-center justify-center text-xs font-black ${
                  idx === 0 ? "bg-[#E05A1D] text-white" : idx === 1 ? "bg-[#A1A1AA] text-[#0F0F0F]" : "bg-[#F59E0B] text-[#0F0F0F]"
                }`}>
                  {idx === 0 ? <Crown size={16} weight="fill" /> : `#${idx + 1}`}
                </div>
              )}

              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-bold text-white uppercase tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {pr.exercise_display}
                  </h3>
                  <p className="text-xs text-[#71717A] mt-0.5">{pr.date}</p>
                </div>
                <button
                  onClick={() => sharePR(pr)}
                  className="text-[#71717A] hover:text-[#E05A1D] transition-colors opacity-50 group-hover:opacity-100 p-1"
                  data-testid={`share-pr-${pr.id}`}
                >
                  {copiedId === pr.id ? <Check size={16} weight="bold" className="text-[#22C55E]" /> : <ShareNetwork size={16} />}
                </button>
              </div>

              <div className="flex items-baseline gap-2">
                <span
                  className={`text-4xl font-black ${idx === 0 ? "text-[#E05A1D]" : "text-white"}`}
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {fromKg(pr.weight).toFixed(1)}
                </span>
                <span className="text-sm text-[#A1A1AA]">{weightLabel}</span>
              </div>

              {pr.previous_weight > 0 && (
                <div className="flex items-center gap-1 mt-2">
                  <TrendUp size={12} className="text-[#22C55E]" weight="bold" />
                  <span className="text-xs text-[#22C55E] font-bold">
                    +{fromKg(pr.weight - pr.previous_weight).toFixed(1)}{weightLabel} from {fromKg(pr.previous_weight).toFixed(1)}{weightLabel}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
